import React, { useState, useEffect, useRef } from "react";
import Webcam from "react-webcam";
import Cropper from "react-easy-crop";
import { showToast } from "../../utils/alert";

interface PhotoCaptureCropperProps {
  photo: File | null;
  existingPhotoUrl?: string | null;
  onPhotoChange: (file: File | null) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

const PhotoCaptureCropper: React.FC<PhotoCaptureCropperProps> = ({
  photo,
  existingPhotoUrl,
  onPhotoChange,
  error,
  required = false,
  disabled = false,
}) => {
  // Camera & Crop states
  const [hasCameraSupport, setHasCameraSupport] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState("");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const webcamRef = useRef<Webcam | null>(null);

  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    setHasCameraSupport(
      !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    );
  }, []);

  useEffect(() => {
    if (!croppedAreaPixels || !imgRef.current || !previewCanvasRef.current) return;
    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      image,
      croppedAreaPixels.x, croppedAreaPixels.y, croppedAreaPixels.width, croppedAreaPixels.height,
      0, 0, croppedAreaPixels.width, croppedAreaPixels.height
    );
  }, [croppedAreaPixels]);

  const handlePhotoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      showToast({ title: "Error", text: "Please upload a valid image file (JPEG, PNG, GIF, SVG).", icon: "error" });
      e.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showToast({ title: "Error", text: "Image must be less than 2 MB.", icon: "error" });
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const startCamera = async () => {
    try {
      const constraints = {
        video: selectedVideoDevice ? { deviceId: selectedVideoDevice } : { facingMode: "user" },
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      setShowCamera(true);

      const devices = await navigator.mediaDevices.enumerateDevices();
      setVideoDevices(devices.filter((d) => d.kind === "videoinput"));
    } catch {
      showToast({ title: "Camera Error", text: "Unable to access camera.", icon: "error" });
    }
  };

  const stopCamera = () => {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setImageToCrop(imageSrc);
        stopCamera();
        setShowCropper(true);
      }
    }
  };

  const saveCroppedImage = async () => {
    if (croppedAreaPixels && previewCanvasRef.current) {
      previewCanvasRef.current.toBlob(
        (blob) => {
          if (!blob) return;
          const file = new File([blob], "student-photo.jpeg", { type: "image/jpeg" });
          if (file.size > 2 * 1024 * 1024) {
            showToast({ title: "Error", text: "Image is larger than 2MB.", icon: "error" });
            return;
          }
          onPhotoChange(file);
          setShowCropper(false);
          setImageToCrop(null);
        },
        "image/jpeg",
        0.8
      );
    }
  };

  const cancelCropping = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  const handleRemovePhoto = () => {
    onPhotoChange(null);
  };

  return (
    <div className="mb-4">
      {/* ══ CROPPER FULLSCREEN OVERLAY ══ */}
      {showCropper && imageToCrop && (
        <div
          className="photo-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1055,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <div
            className="photo-modal-content bg-white"
            style={{
              borderRadius: 8,
              padding: "28px 32px",
              width: "min(96vw, 760px)",
              maxHeight: "92vh",
              overflowY: "auto",
            }}
          >
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: "#3D5EE1",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <i className="ti ti-crop text-white" style={{ fontSize: 18 }} />
                </span>
                <div>
                  <h5 className="mb-0 fw-semibold photo-modal-title">Crop Photo</h5>
                  <small className="text-muted">Drag image to adjust • Scroll to zoom</small>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={cancelCropping}
                aria-label="Close"
              />
            </div>

            {/* Crop area + preview side by side */}
            <div className="row g-4">
              <div className="col-md-8">
                <div
                  className="photo-crop-container"
                  style={{
                    position: "relative",
                    width: "100%",
                    height: 400,
                    borderRadius: 5,
                    overflow: "hidden",
                  }}
                >
                  {/* @ts-ignore - TS moduleResolution: "node" fails to resolve .mts types properly */}
                  <Cropper
                    image={imageToCrop}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(croppedArea: any, pixels: any) => setCroppedAreaPixels(pixels)}
                  />
                </div>
                
                {/* Hidden image source for canvas drawing */}
                <img 
                  ref={imgRef} 
                  src={imageToCrop} 
                  alt="Hidden Source" 
                  style={{ display: "none" }} 
                />
              </div>

              {/* Live circular preview */}
              <div className="col-md-4 d-flex flex-column align-items-center justify-content-center gap-3">
                <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 12, letterSpacing: "0.05em" }}>LIVE PREVIEW</p>
                <div
                  className="photo-live-preview"
                  style={{
                    width: 140, height: 140,
                    borderRadius: "50%",
                    overflow: "hidden",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "2px solid #E9EDF4",
                  }}
                >
                  {croppedAreaPixels ? (
                    <canvas
                      ref={previewCanvasRef}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <i className="ti ti-user" style={{ fontSize: 56, color: "#9CA1AF" }} />
                  )}
                </div>

                {/* Hidden data canvas */}
                {!croppedAreaPixels && (
                  <canvas ref={previewCanvasRef} className="d-none" />
                )}

                <div className="text-center">
                  <small className="text-muted d-block">Drag the image on the</small>
                  <small className="text-muted d-block">left to adjust your crop</small>
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 photo-modal-footer">
              <button
                type="button"
                className="btn btn-light px-4"
                onClick={cancelCropping}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary px-4 fw-semibold"
                onClick={saveCroppedImage}
                disabled={!croppedAreaPixels}
              >
                <i className="ti ti-check me-1" />
                Use This Photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ CAMERA FULLSCREEN OVERLAY ══ */}
      {showCamera && (
        <div
          className="photo-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1055,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <div
            className="photo-modal-content bg-white"
            style={{
              borderRadius: 8,
              padding: "28px 32px",
              width: "min(96vw, 620px)",
              maxHeight: "92vh",
              overflowY: "auto",
            }}
          >
            {/* Camera header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: "#3D5EE1",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <i className="ti ti-camera text-white" style={{ fontSize: 18 }} />
                </span>
                <div>
                  <h5 className="mb-0 fw-semibold photo-modal-title">Take Photo</h5>
                  <small className="text-muted">Position your face within the frame</small>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={stopCamera}
                aria-label="Close"
              />
            </div>

            {/* Video feed */}
            <div
              style={{
                borderRadius: 5,
                overflow: "hidden",
                position: "relative",
                background: "#000",
                height: 400,
              }}
            >
              <Webcam
                key={selectedVideoDevice || 'default'}
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={
                  selectedVideoDevice
                    ? { deviceId: { exact: selectedVideoDevice } }
                    : { facingMode: "user" }
                }
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              {/* Face guide overlay */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    width: 220, height: 280,
                    borderRadius: "50%",
                    border: "2px dashed rgba(255,255,255,0.7)",
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.3)",
                  }}
                />
              </div>
            </div>

            {/* Camera switcher */}
            {videoDevices.length > 1 && (
              <select
                className="form-select form-select-sm mt-3"
                value={selectedVideoDevice}
                onChange={(e) => setSelectedVideoDevice(e.target.value)}
              >
                <option value="">Default Camera</option>
                {videoDevices.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${i + 1}`}
                  </option>
                ))}
              </select>
            )}

            {/* Footer actions matching crop modal */}
            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 photo-modal-footer">
              <button
                type="button"
                className="btn btn-light px-4"
                onClick={stopCamera}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary px-4 fw-semibold"
                onClick={capturePhoto}
              >
                <i className="ti ti-camera me-1" />
                Take Photo
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ══ DEFAULT UPLOAD ZONE ══ */}
      <div
        className={`student-photo-upload-zone ${error ? "has-error border-danger" : photo || existingPhotoUrl ? "has-file" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 24,
          borderRadius: 8,
          padding: "20px 24px",
          transition: "border-color 0.2s",
          border: `1px dashed ${error ? "#E82646" : "#E9EDF4"}`,
          background: "#F9FAFB",
        }}
      >
        {/* Avatar circle */}
        <div
          className={`student-photo-avatar ${photo || existingPhotoUrl ? "has-file" : ""}`}
          style={{
            width: 96, height: 96, borderRadius: "50%",
            overflow: "hidden", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.3s",
            background: "#fff",
            border: "2px solid #E9EDF4",
          }}
        >
          {photo ? (
            <img
              src={URL.createObjectURL(photo)}
              alt="Student preview"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : existingPhotoUrl ? (
            <img
              src={existingPhotoUrl}
              alt="Student preview"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.src = "/assets/img/profiles/default-male.png";
              }}
            />
          ) : (
            <i className="ti ti-user" style={{ fontSize: 44, color: "#9EAFF0" }} />
          )}
        </div>

        {/* Info + actions */}
        <div className="flex-grow-1">
          <div className="mb-1">
            <span className="fw-semibold student-photo-title" style={{ fontSize: 15 }}>
              Student Photo
            </span>
            {!existingPhotoUrl && !photo && required && (
              <span
                className="ms-2 badge"
                style={{ background: "#FDE9ED", color: "#E82646", fontWeight: 600, fontSize: 11 }}
              >
                Required
              </span>
            )}
            {(photo || existingPhotoUrl) && (
              <span
                className="ms-2 badge"
                style={{ background: "#E8F9E8", color: "#1ABE17", fontWeight: 600, fontSize: 11 }}
              >
                ✓ {photo ? "Ready" : "Uploaded"}
              </span>
            )}
          </div>
          <p className="mb-3" style={{ fontSize: 13, color: "#6A7287" }}>
            Upload a clear passport-style photo (JPEG, PNG, GIF, SVG, max 2MB) or take one with your camera
          </p>
          {error && (
            <p className="mb-3 fw-semibold" style={{ fontSize: 13, color: "#E82646", marginTop: "-10px" }}>
              <i className="ti ti-alert-circle me-1" />
              {error}
            </p>
          )}

          {/* Action buttons row */}
          {!disabled && (
            <div className="d-flex flex-wrap gap-2">
              {/* Upload */}
              <label
                htmlFor="student-photo-input"
                className="mb-0"
                style={{ cursor: "pointer" }}
              >
                <span className="btn btn-primary btn-sm fw-semibold">
                  <i className="ti ti-upload me-1" />
                  {photo || existingPhotoUrl ? "Change Photo" : "Upload File"}
                </span>
              </label>
              <input
                id="student-photo-input"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/svg+xml,.jpeg,.jpg,.png,.gif,.svg"
                style={{ display: "none" }}
                onChange={handlePhotoFileChange}
              />

              {/* Camera */}
              {hasCameraSupport && (
                <button
                  type="button"
                  className="btn btn-light btn-sm fw-semibold"
                  onClick={startCamera}
                >
                  <i className="ti ti-camera me-1" style={{ color: "#3D5EE1" }} />
                  Take Photo
                </button>
              )}

              {/* Remove */}
              {photo && (
                <button
                  type="button"
                  className="btn btn-sm student-photo-remove-btn"
                  style={{
                    borderRadius: 4,
                    border: "1px solid #E9EDF4",
                    background: "#fff",
                  }}
                  onClick={handleRemovePhoto}
                >
                  <i className="ti ti-trash me-1 text-danger" />
                  Remove
                </button>
              )}
            </div>
          )}

          {/* File info / hint */}
          <div className="mt-2 d-flex align-items-center gap-1">
            <i className="ti ti-info-circle" style={{ fontSize: 13, color: "#9CA1AF" }} />
            <small style={{ color: "#9CA1AF", fontSize: 12 }}>
              {photo
                ? `${photo.name} · ${(photo.size / 1024).toFixed(0)} KB`
                : existingPhotoUrl
                  ? "Existing Photo"
                  : "JPEG, PNG, GIF, SVG · Max 2 MB"}
            </small>
          </div>
        </div>
      </div>
      
      {/* Dark Mode Styles */}
      <style>{`
        [data-theme="dark"] .student-photo-upload-zone,
        [data-theme="darks"] .student-photo-upload-zone,
        [data-theme-mode="dark"] .student-photo-upload-zone,
        .dark-data-theme .student-photo-upload-zone {
          background: #1e1e2d !important;
          border-color: #323246 !important;
        }
        [data-theme="dark"] .student-photo-upload-zone .student-photo-avatar,
        [data-theme="darks"] .student-photo-upload-zone .student-photo-avatar,
        [data-theme-mode="dark"] .student-photo-upload-zone .student-photo-avatar,
        .dark-data-theme .student-photo-upload-zone .student-photo-avatar {
          background: #2b2b40 !important;
          border-color: #323246 !important;
        }
        [data-theme="dark"] .student-photo-upload-zone .student-photo-title,
        [data-theme="darks"] .student-photo-upload-zone .student-photo-title,
        [data-theme-mode="dark"] .student-photo-upload-zone .student-photo-title,
        .dark-data-theme .student-photo-upload-zone .student-photo-title {
          color: #e5e5e5 !important;
        }
        [data-theme="dark"] .student-photo-upload-zone p,
        [data-theme="darks"] .student-photo-upload-zone p,
        [data-theme-mode="dark"] .student-photo-upload-zone p,
        .dark-data-theme .student-photo-upload-zone p {
          color: #9ca3af !important;
        }
        [data-theme="dark"] .photo-modal-content,
        [data-theme="darks"] .photo-modal-content,
        [data-theme-mode="dark"] .photo-modal-content,
        .dark-data-theme .photo-modal-content {
          background: #1e1e2d !important;
          border-color: #323246 !important;
        }
        [data-theme="dark"] .photo-modal-content .text-dark,
        [data-theme="darks"] .photo-modal-content .text-dark,
        [data-theme-mode="dark"] .photo-modal-content .text-dark,
        .dark-data-theme .photo-modal-content .text-dark {
          color: #e5e5e5 !important;
        }
        [data-theme="dark"] .photo-modal-header,
        [data-theme="darks"] .photo-modal-header,
        [data-theme-mode="dark"] .photo-modal-header,
        .dark-data-theme .photo-modal-header {
          border-bottom-color: #323246 !important;
        }
        [data-theme="dark"] .photo-modal-footer,
        [data-theme="darks"] .photo-modal-footer,
        [data-theme-mode="dark"] .photo-modal-footer,
        .dark-data-theme .photo-modal-footer {
          border-top-color: #323246 !important;
        }
        [data-theme="dark"] .student-photo-remove-btn,
        [data-theme="darks"] .student-photo-remove-btn,
        [data-theme-mode="dark"] .student-photo-remove-btn,
        .dark-data-theme .student-photo-remove-btn {
          background: #2b2b40 !important;
          border-color: #323246 !important;
          color: #e5e5e5 !important;
        }
      `}</style>
    </div>
  );
};

export default PhotoCaptureCropper;
